import {
  NanoIcon,
  iconNames,
  getIconLayerColors,
  type IconName,
} from "@-/dev.improve.haniverse.assets.icons";
import { Platform, StyleSheet, type ColorValue } from "@-/com.npmjs.react-native";
import type { IconProps } from "@-/com.npmjs.react-native-nano-icons";
import { useResolveClassNames, withUniwind } from "@-/com.npmjs.uniwind";
import { GlassContentContext, getGlassInkColor } from "@-/dev.improve.haniverse.components.ui.text";
import { use, useMemo } from "react";

export type { IconName };
// Nano Icons' web renderer already accepts className and expects a plain style object.
function NativeNanoIcon({ style, ...props }: IconProps<string>) {
  // Uniwind supplies layout styles; glyph size owns width/height so an explicit
  // size prop keeps taking precedence over a size utility.
  const layoutStyle = { ...StyleSheet.flatten(style) };
  delete layoutStyle.width;
  delete layoutStyle.height;
  return <NanoIcon {...props} style={layoutStyle} />;
}
const StyledNanoIcon = Platform.OS === "web" ? NanoIcon : withUniwind(NativeNanoIcon);
export type IconBaseProps = Omit<IconProps<IconName>, "name" | "color"> & { color?: ColorValue };

export function Icon({
  className,
  color,
  name,
  size,
  ...props
}: IconBaseProps & { name: IconName }) {
  const glyphName = iconNames[name];
  if (glyphName === undefined) {
    throw new Error('Icon: no component for icon name "' + String(name) + '"');
  }
  const resolvedStyle = useResolveClassNames(className ?? "");
  const glassAppearance = use(GlassContentContext);
  const glassInk =
    glassAppearance === null ? undefined : getGlassInkColor(className, glassAppearance);
  // Web inherits CSS ink, keeping server markup and hydration identical. Native glyphs
  // need an explicit color; classless icons follow the same theme as ordinary text.
  const defaultInk = useResolveClassNames("text-foreground").color;
  const resolvedColor =
    color ?? glassInk ?? (Platform.OS === "web" ? undefined : (resolvedStyle.color ?? defaultInk));
  const layerColors = useMemo(() => getIconLayerColors(name, resolvedColor), [name, resolvedColor]);
  const accessible = props.accessible ?? props.accessibilityLabel !== undefined;
  const resolvedSize =
    size ??
    (typeof resolvedStyle.width === "number"
      ? resolvedStyle.width
      : typeof resolvedStyle.height === "number"
        ? resolvedStyle.height
        : 24);
  return (
    <StyledNanoIcon
      {...props}
      accessibilityLabel={props.accessibilityLabel ?? (accessible ? name : "")}
      accessible={accessible}
      allowFontScaling={props.allowFontScaling ?? false}
      className={className ?? "text-foreground"}
      color={layerColors}
      name={glyphName}
      size={resolvedSize}
      tintMode={Array.isArray(layerColors) ? "all" : "currentColor"}
    />
  );
}
